<script lang="ts">
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { RotateCcw } from 'lucide-svelte';
  import { hrDevicesApi } from '$lib/api/devices';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import StatusBadge from '$lib/components/ui/StatusBadge.svelte';
  import DataTable from '$lib/components/ui/DataTable.svelte';
  import type { DeviceRow } from '$lib/types';

  const queryClient = useQueryClient();
  const query = createQuery(() => ({
    queryKey: ['hr-devices'],
    queryFn: () => hrDevicesApi.list()
  }));

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['hr-devices'] });
  }

  async function reset(userId: number, name: string) {
    if (!confirm(`Reset the registered device for ${name}? They'll be able to bind a new device on their next login.`))
      return;
    try {
      await hrDevicesApi.reset(userId);
      refresh();
      toast.success('Device reset.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  function fmt(dt: string | null): string {
    return dt ? new Date(dt).toLocaleString() : '—';
  }
</script>

<div class="tym-page">
  <p class="tym-section-sub mb-4">
    Anti-buddy-punching: each account is bound to exactly one device. Reset a user's device if they changed phones.
  </p>

  {#if query.isLoading}
    <div class="tym-loading-state">Loading...</div>
  {:else if query.isError}
    <div class="tym-error-state">Could not load devices.</div>
  {:else if query.data}
    <DataTable
      mobileCards
      mobileColumns={['user', 'registered', 'device', 'last_seen']}
      columns={[
        { key: 'user', label: 'User', cell: userCell },
        { key: 'registered', label: 'Registered', cell: registeredCell },
        { key: 'device', label: 'Device', cell: deviceCell },
        { key: 'registered_on', label: 'Registered on', cell: registeredOnCell },
        { key: 'last_seen', label: 'Last seen', cell: lastSeenCell },
        { key: 'actions', label: '', align: 'right' as const, cell: actionsCell }
      ]}
      rows={query.data}
      rowKey={(row) => row.user_id}
      emptyTitle="No users found"
      emptyBody="Devices bind automatically the first time someone logs in."
    />
  {/if}
</div>

{#snippet userCell(row: DeviceRow)}
  <span class="font-medium text-navy">{row.user_name || row.user_email}</span>
{/snippet}
{#snippet registeredCell(row: DeviceRow)}
  {#if row.registered}
    <StatusBadge kind="info" label="Registered" />
  {:else}
    <span class="muted">Not registered</span>
  {/if}
{/snippet}
{#snippet deviceCell(row: DeviceRow)}
  <span class="muted">{row.user_agent || '—'}</span>
{/snippet}
{#snippet registeredOnCell(row: DeviceRow)}
  <span class="muted">{fmt(row.created_at)}</span>
{/snippet}
{#snippet lastSeenCell(row: DeviceRow)}
  <span class="muted">{fmt(row.last_seen_at)}</span>
{/snippet}
{#snippet actionsCell(row: DeviceRow)}
  {#if row.registered}
    <div class="flex justify-end">
      <button type="button" class="btn-secondary" onclick={() => reset(row.user_id, row.user_name || row.user_email)}>
        <RotateCcw class="h-3.5 w-3.5" /> Reset
      </button>
    </div>
  {/if}
{/snippet}
